import { Div, Drawer, InfoCard, P, Skeleton } from '@dnb/eufemia';
import { clock_medium as ClockIcon, people_1 as TeamIcon, person as UserIcon } from '@dnb/eufemia/icons';
import { type ApiType } from '@portals/shared/portal/ApiDto';
import { Decision } from '@portals/shared/portal/AppApiEntitlementDto';
import { type JSX, useMemo } from 'react';

import { ExpandableCard } from '@/components/ExpandableCard';
import { useBackend } from '@/hooks/useBackend';
import type { EntitlementRequestResponse } from '@/openapi/schema';
import { useGroupedScopes } from '@/pages/profile/apps/application/components/EntitlementRequest';
import { EntitlementRequestDetails } from '@/pages/profile/apps/application/components/EntitlementRequest/components/EntitlementRequestDetails';
import { apiOrEvent } from '@/utils/integrationServiceUtils';

import style from './index.module.css';

function RequestTile({ request }: { request: EntitlementRequestResponse }): JSX.Element {
  const ownerLabel =
    request.owner.type === 'TEAM' ? (
      <>
        <TeamIcon /> <b>{request.owner.name}</b>
      </>
    ) : (
      <>
        <UserIcon /> <b>{request.owner.email}</b>
      </>
    );

  const groupedRequest = useGroupedScopes({
    scopes: (request.scopes ?? []).map((scope) => ({ id: '', name: scope, description: '' })),
    testScopes: (request.testScopes ?? []).map((scope) => ({ id: '', name: scope, description: '' })),
  });

  const requestProps = useMemo(
    () => ({
      scopes: request.scopes ?? [],
      testScopes: request.testScopes ?? [],
      externalTicketId: request.externalTicketId,
      createdAt: request.createdAt,
      decision: Decision.PENDING,
      decisionMadeAt: null,
      decisionComment: null,
      externalTicketComment: null,
    }),
    [request],
  );

  return (
    <Div className={style['RequestTile']}>
      <div className={style['TileContent']}>
        <div>
          <P weight="medium">{request.appName}</P>
          <P className={style['OwnerLabel']} size="small" top="xx-small">
            Requested by: {ownerLabel}
          </P>
        </div>
        <Drawer
          omitTriggerButton={false}
          title="Request details"
          triggerAttributes={{ text: 'View', variant: 'secondary' }}
        >
          <Drawer.Navigation />
          <Drawer.Header>
            <P>
              The list below displays the scopes requested by {ownerLabel} for the <b>{request.appName}</b> application
              with the use case: &quot;{request.useCase}&quot;
            </P>
            <P bottom top="small">
              {request.externalTicketId
                ? 'The request needs to be approved by the person listed below in ServiceNow.'
                : 'The request needs to be approved in ServiceNow.'}
            </P>
          </Drawer.Header>
          <Drawer.Body>
            <EntitlementRequestDetails
              apiName={request.apiName}
              groupedRequest={groupedRequest}
              requestProps={requestProps}
            />
          </Drawer.Body>
        </Drawer>
      </div>
    </Div>
  );
}

type ProviderEntitlementRequestsProps = {
  apiSlug: string;
  apiType: ApiType;
};

export default function ProviderEntitlementRequests({
  apiSlug,
  apiType,
}: ProviderEntitlementRequestsProps): JSX.Element {
  const { data: requests, isLoading: loading } = useBackend('/apis/{apiSlug}/entitlement-requests', {
    params: { path: { apiSlug }, query: { status: 'PENDING' } },
  });

  const sortedRequests = useMemo(() => {
    if (!requests) return [];
    return [...requests].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [requests]);

  if (!loading && sortedRequests.length === 0) {
    return (
      <InfoCard bottom="small" stretch text={`This ${apiOrEvent(apiType)} currently has no pending access requests.`} />
    );
  }

  const nodes = sortedRequests.map((request) => <RequestTile key={request.id} request={request} />);

  return (
    <Skeleton show={loading}>
      <div className={style['Card']}>
        <ExpandableCard
          emptyNodesText="No pending requests"
          nodes={nodes}
          title={
            <P className={style['Title']} weight="medium">
              <ClockIcon /> The following apps have requested access to the {apiOrEvent(apiType)}
            </P>
          }
          wrapLimit={1}
        />
      </div>
    </Skeleton>
  );
}
